// =============================================================================
// BP-05-03: Calendar Frontend - QuestionsList Component
// =============================================================================

import React, { useState } from 'react';
import { HelpCircle, MessageSquare, Plus, Trash2, Check } from 'lucide-react';
import { type QuestionItem } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface QuestionsListProps {
  questions: QuestionItem[];
  onAnswer?: (questionId: string, answer: string) => void;
  onAdd?: (question: string) => void;
  onRemove?: (questionId: string) => void;
  readOnly?: boolean;
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function QuestionsList({
  questions,
  onAnswer,
  onAdd,
  onRemove,
  readOnly = false,
  className,
}: QuestionsListProps) {
  const [newQuestion, setNewQuestion] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingAnswer, setEditingAnswer] = useState('');

  const handleAdd = () => {
    if (newQuestion.trim() && onAdd) {
      onAdd(newQuestion.trim());
      setNewQuestion('');
    }
  };

  const startEditing = (question: QuestionItem) => {
    setEditingId(question.id);
    setEditingAnswer(question.answer || '');
  };

  const saveAnswer = () => {
    if (editingId && onAnswer) {
      onAnswer(editingId, editingAnswer);
      setEditingId(null);
      setEditingAnswer('');
    }
  };

  const answeredCount = questions.filter((q) => q.answer).length;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Summary */}
      {questions.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-warm-gray">
          <HelpCircle className="w-4 h-4" />
          <span>
            {answeredCount} of {questions.length} questions answered
          </span>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-3">
        {questions.map((question) => (
          <div
            key={question.id}
            className={cn(
              'p-4 rounded-xl border transition-colors',
              question.answer
                ? 'bg-sage/5 border-sage/20'
                : 'bg-cream border-transparent'
            )}
          >
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                  question.answer ? 'bg-sage/20' : 'bg-warm-gray/20'
                )}
              >
                {question.answer ? (
                  <Check className="w-3.5 h-3.5 text-sage" />
                ) : (
                  <HelpCircle className="w-3.5 h-3.5 text-warm-gray" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-navy">{question.question}</p>

                {/* Answer Display/Edit */}
                {editingId === question.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={editingAnswer}
                      onChange={(e) => setEditingAnswer(e.target.value)}
                      placeholder="Type the answer..."
                      rows={2}
                      className="w-full px-3 py-2 bg-white border border-sage/30 rounded-lg text-sm focus:outline-none focus:border-sage resize-none"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveAnswer}
                        className="px-3 py-1.5 bg-sage text-white text-sm rounded-lg hover:bg-sage-dark transition-colors"
                      >
                        Save Answer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setEditingAnswer('');
                        }}
                        className="px-3 py-1.5 text-warm-gray text-sm hover:bg-cream rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : question.answer ? (
                  <div className="mt-2">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-sage mt-0.5" />
                      <p className="text-sm text-navy">{question.answer}</p>
                    </div>
                    {!readOnly && onAnswer && (
                      <button
                        type="button"
                        onClick={() => startEditing(question)}
                        className="mt-2 text-xs text-sage hover:text-sage-dark transition-colors"
                      >
                        Edit answer
                      </button>
                    )}
                  </div>
                ) : !readOnly && onAnswer ? (
                  <button
                    type="button"
                    onClick={() => startEditing(question)}
                    className="mt-2 text-sm text-sage hover:text-sage-dark transition-colors"
                  >
                    + Add answer
                  </button>
                ) : null}
              </div>

              {!readOnly && onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(question.id)}
                  className="p-1 text-warm-gray hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add New */}
      {!readOnly && onAdd && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder="Add a question to ask..."
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
            className="flex-1 px-3 py-2 bg-cream rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage/20"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newQuestion.trim()}
            className="px-3 py-2 bg-sage/10 text-sage hover:bg-sage/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {questions.length === 0 && readOnly && (
        <p className="text-sm text-warm-gray text-center py-4">
          No questions for this appointment
        </p>
      )}
    </div>
  );
}

export default QuestionsList;
